import { Button } from "@/components/ui/Button";

export interface ConflictBannerProps {
  message: string;
  colSpan: number;
  onKeepMine: () => void;
  onKeepTheirs: () => void;
}

export interface ConflictNoticeProps {
  message: string;
  onKeepMine: () => void;
  onKeepTheirs: () => void;
}

/**
 * The message and the two buttons — the whole conflict surface (spec
 * judgement call 4), factored out so `ConflictBanner` (a table row) and
 * `ConflictNotice` (the record page's own container, Story 2.5, VC-2.5-11)
 * are two thin wrappers around one real implementation, never two
 * implementations of the conflict UI itself. Neither wrapper adds or
 * changes behavior — only the surrounding markup a `<table>` versus a
 * plain field-row list each requires.
 *
 * `role="alert"` (review finding: this banner previously had no
 * accessibility wiring of its own beyond the shared `aria-live` region's
 * plain-text announcement) — its implicit `aria-live="assertive"` means a
 * screen reader announces the banner's own text, including "Keep mine"/
 * "Keep theirs", the moment it appears, without depending on the visual
 * layout to convey that a decision is now required.
 */
function ConflictBannerBody({ message, onKeepMine, onKeepTheirs }: ConflictNoticeProps) {
  return (
    <div role="alert" className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-body text-ink">{message}</p>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={onKeepMine}>
          Keep mine
        </Button>
        <Button variant="ghost" onClick={onKeepTheirs}>
          Keep theirs
        </Button>
      </div>
    </div>
  );
}

/**
 * The save-conflict surface (spec judgement call 4): an inline banner beside
 * the affected row — a sibling `<tr>` immediately after it, never a
 * modal/dialog (`DESIGN.md`: errors "render next to the thing that failed").
 * Names both values by rendering `message` verbatim, whatever the caller
 * (a route, e.g. `UsersList`) built it as — VC-2.3-11's API response always
 * names both values, directly in prose or structurally via `field`/
 * `current_value` (Story 2.3 review round 2), and a caller with a
 * plain-language label map for its own enum values (`ROLE_LABELS`) may
 * re-derive a relabeled sentence from those before ever handing `message`
 * here, rather than this generic component importing an entity-specific
 * label map itself (the same "no route-specific branch" rule the rest of
 * `DataTable` already follows). Either way, this component itself formats
 * nothing — it only ever displays what it was given.
 */
export function ConflictBanner({ message, colSpan, onKeepMine, onKeepTheirs }: ConflictBannerProps) {
  return (
    <tr>
      <td colSpan={colSpan} className="border-b border-border-hairline bg-warning-quiet px-4 py-3">
        <ConflictBannerBody message={message} onKeepMine={onKeepMine} onKeepTheirs={onKeepTheirs} />
      </td>
    </tr>
  );
}

/**
 * The record page's own container for the identical banner (Story 2.5,
 * VC-2.5-11: "the same named conflict surface... reusing that component or
 * its exact behavior"). A field row is not a table row, so this cannot be
 * `<tr>`-shaped like `ConflictBanner` — but the message and the two buttons
 * are the exact same `ConflictBannerBody`, not a second implementation.
 */
export function ConflictNotice({ message, onKeepMine, onKeepTheirs }: ConflictNoticeProps) {
  return (
    <div className="mb-4 rounded-md border border-border-hairline bg-warning-quiet px-4 py-3">
      <ConflictBannerBody message={message} onKeepMine={onKeepMine} onKeepTheirs={onKeepTheirs} />
    </div>
  );
}
